import React, {
  createContext,
  useState,
  useEffect,
  FunctionComponent,
  useContext,
} from "react";
import { Center, Spinner } from "@chakra-ui/react"
import { LoginMutation_login, SessionState, UserPreferenceMutation_userPreference } from "graphql/generated";
import { useApolloClient, useMutation } from "@apollo/client";
import { IntlProvider } from "react-intl";

import { envVars } from "common/env";
import { AUTH_TOKEN_KEY_NAME } from "common/consts";
import { SESSION_STATE_QUERY } from "graphql/queries";
import { LOGOUT_MUTATION } from "graphql/mutations";
import { USER_PREFERENCE_DEFAULTS } from "common/user-preferences/defaults";

const DEFAULT_LOCALE = USER_PREFERENCE_DEFAULTS.ui__locale;

interface ILocaleData {
  locale: string;
  messages: Record<string, string>;
}

interface ILocaleDef {
  locale: string;
  label: string;
}

interface IGlobalState {
  user: LoginMutation_login | null;
  setUserPreferences: (preferences: UserPreferenceMutation_userPreference) => void;
  login: (user: LoginMutation_login) => void;
  logout: () => void;
  setLocale: (locale: string) => void;
  locale: string;
  locales: Array<ILocaleDef>;
  connected: boolean;
  setConnected: (status: boolean) => void;
}


const LOCALES: Array<ILocaleDef> = [
  { locale: "en", label: "English" },
  { locale: "cs", label: "Čeština" },
  // { locale: "pl", label: "Polski" },
];

const GlobalStateContext = createContext<IGlobalState>({} as IGlobalState);

export const GlobalStateProvider: FunctionComponent<{}> = ({ children }) => {
  const [isInit, setInitFlag] = useState<boolean>(false);
  const [connected, setConnected] = useState<boolean>(true);
  const [user, setUser] = useState<LoginMutation_login | null>(null);
  const [locale, setLocale] = useState<string>(DEFAULT_LOCALE);
  const [localeData, setLocaleData] = useState<ILocaleData>({
    locale: locale,
    messages: {},
  });
  const { query } = useApolloClient();
  const [logoutMutation] = useMutation(LOGOUT_MUTATION);

  useEffect(() => {
    const authToken = localStorage.getItem(AUTH_TOKEN_KEY_NAME);

    if (!authToken) {
      setInitFlag(true);
      return;
    }

    query<SessionState>({
      query: SESSION_STATE_QUERY,
    })
      .then((response) => {
        setUser(response.data.sessionState.user);
        setInitFlag(true);
      })
      .catch((error) => {
        console.log("error", error);

        setInitFlag(true);
      });
  }, [query]);

  useEffect(() => {
    fetch(`/lang/${locale}.json?v${envVars.VERSION}`)
      .then((response) => response.json())
      .then((response) => {
        setLocaleData({ locale, messages: response });
      })
      .catch(() => {
        alert(`Unable to load language "${locale}"`);
      });
  }, [user?.token, locale]);

  const login = (loginUser: LoginMutation_login) => {
    localStorage.setItem(AUTH_TOKEN_KEY_NAME, loginUser.token);
    setUser(loginUser);
  };

  const setUserPreferences = (preferences: UserPreferenceMutation_userPreference) => {
    setUser(current => !current ? null : {
      ...current,
      preferences,
    });
  };

  const logout = () => {
    const cleanup = () => {
      localStorage.removeItem(AUTH_TOKEN_KEY_NAME);
      setLocale(DEFAULT_LOCALE);
      setUser(null);
    };

    if (!localStorage.getItem(AUTH_TOKEN_KEY_NAME)) {
      cleanup();
      return;
    }

    logoutMutation()
      .catch((error) => {
        console.warn("logout mutation failed", error);
      })
      .finally(cleanup);
  };

  useEffect(() => {
    if (user?.preferences?.ui__locale) {
      setLocale(user.preferences.ui__locale);
    }
  }, [user?.preferences?.ui__locale]);

  if (!isInit) {
    return (
      <Center mt={10}>
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <GlobalStateContext.Provider
      value={{
        user,
        setUserPreferences,
        login,
        logout,
        setLocale,
        locale,
        locales: LOCALES,
        connected,
        setConnected,
      }}
    >
      <IntlProvider
        messages={localeData.messages}
        locale={localeData.locale}
        defaultLocale={DEFAULT_LOCALE}
      >
        {children}
      </IntlProvider>
    </GlobalStateContext.Provider>
  );
};

export const useGlobalState = (): IGlobalState => {
  return useContext(GlobalStateContext);
};
